<template>
  <v-app>
    <!-- Header -->
    <v-app-bar color="primary" density="default" elevation="2">
      <v-container class="d-flex align-center px-2 px-sm-6">
        <!-- Logo and church name -->
        <div class="d-flex align-center" style="min-width: 200px; flex: 1">
          <v-img
            src="/images/header/ngk-eemdijk-logo-green.jpg"
            alt="NGK Eemdijk Logo"
            max-height="180"
            max-width="180"
            contain
            class="me-3"
          ></v-img>
        </div>

        <v-spacer></v-spacer>

        <!-- Desktop Navigation -->
        <div class="d-none d-md-flex">
          <template v-for="(item, index) in navigationItems" :key="index">
            <v-btn
              v-if="item.to"
              :to="item.to"
              class="mx-2 app-toolbar-action"
              variant="text"
              :ripple="false"
            >
              {{ item.title }}
            </v-btn>
            <v-btn
              v-else-if="item.href"
              :href="item.href"
              class="mx-2 app-toolbar-action"
              variant="text"
              :ripple="false"
            >
              {{ item.title }}
            </v-btn>
          </template>
        </div>

        <!-- Mobile menu button -->
        <v-app-bar-nav-icon
          class="d-flex d-md-none app-toolbar-action"
          @click="drawer = !drawer"
        ></v-app-bar-nav-icon>
      </v-container>
    </v-app-bar>

    <!-- Mobile Navigation Drawer -->
    <v-navigation-drawer v-model="drawer" temporary location="right">
      <v-list>
        <template v-for="(item, index) in navigationItems" :key="index">
          <v-list-item v-if="item.to" :to="item.to" color="primary" class="mb-2">
            <template v-slot:prepend>
              <v-icon :icon="item.icon"></v-icon>
            </template>
            <v-list-item-title>{{ item.title }}</v-list-item-title>
          </v-list-item>
          <v-list-item v-else-if="item.href" :href="item.href" color="primary" class="mb-2">
            <template v-slot:prepend>
              <v-icon :icon="item.icon"></v-icon>
            </template>
            <v-list-item-title>{{ item.title }}</v-list-item-title>
          </v-list-item>
        </template>
      </v-list>
    </v-navigation-drawer>

    <!-- Main Content -->
    <v-main>
      <RouterView />
    </v-main>

    <!-- Footer -->
    <v-footer class="bg-primary app-on-primary">
      <v-container>
        <v-row>
          <v-col cols="12" md="4">
            <h3 class="text-h6 mb-3">NGK Eemdijk</h3>
            <v-list density="compact" bg-color="transparent" class="app-on-primary pa-0">
              <v-list-item
                v-for="(item, i) in footerItems"
                :key="i"
                class="px-0 app-on-primary"
                :value="item.value"
              >
                <template v-slot:prepend>
                  <v-icon :icon="item.icon" class="me-3 app-on-primary"></v-icon>
                </template>
                <v-list-item-title class="app-on-primary">
                  {{ item.title }}: {{ item.value }}
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-col>
          <v-col cols="12" md="4">
            <h3 class="text-h6 mb-3">Diensten</h3>
            <p>{{ sundayServiceText }}</p>
          </v-col>
          <v-col cols="12" md="4">
            <h3 class="text-h6 mb-3">Socials</h3>
            <div class="d-flex">
              <v-btn
                icon
                variant="text"
                class="me-3 app-toolbar-action"
                href="https://www.youtube.com/@gkveemdijk2925/streams"
                target="_blank"
              >
                <v-icon>mdi-youtube</v-icon>
              </v-btn>
              <v-btn icon variant="text" class="me-3 app-toolbar-action">
                <v-icon>mdi-facebook</v-icon>
              </v-btn>
            </div>
          </v-col>
        </v-row>
        <v-row class="mt-3">
          <v-col cols="12" class="text-center text-caption">
            © {{ new Date().getFullYear() }} NGK Eemdijk. Alle rechten voorbehouden.
          </v-col>
        </v-row>
      </v-container>
    </v-footer>
  </v-app>
</template>

<script setup lang="ts">
import { RouterView } from 'vue-router';
import { computed, onMounted, ref } from 'vue';
import { useAuthStore } from './features/auth/authStore';
import { getSundayServiceText } from './shared/serviceTimes';

// Navigation drawer control
const drawer = ref(false);

const auth = useAuthStore();
const sundayServiceText = getSundayServiceText();

onMounted(() => {
  void auth.refresh();
});

// Navigation items
type NavigationItem = {
  title: string;
  icon: string;
  to?: string;
  href?: string;
};

const navigationItems = computed<NavigationItem[]>(() => {
  const items: NavigationItem[] = [
    {
      title: 'Over ons',
      icon: 'mdi-account-group',
      to: '/over-ons'
    },
    {
      title: 'Kerkdiensten',
      icon: 'mdi-church',
      to: '/kerkdiensten'
    },
    {
      title: 'Contact',
      icon: 'mdi-email',
      to: '/contact'
    }
  ];

  if (auth.canUploadPreekrooster) {
    items.push({
      title: 'Preekrooster',
      icon: 'mdi-file-upload',
      to: '/preekrooster'
    });
  }

  if (auth.isAuthenticated) {
    items.push({
      title: 'Uitloggen',
      icon: 'mdi-logout',
      href: '/.auth/logout?post_logout_redirect_uri=/'
    });
  } else {
    items.push({
      title: 'Inloggen',
      icon: 'mdi-login',
      to: '/login'
    });
  }

  return items;
});

// Footer information
const footerItems = [
  {
    title: 'Adres',
    icon: 'mdi-map-marker',
    value: 'Eemdijk 63, 3754 ND Eemdijk'
  },
  {
    title: 'Tel',
    icon: 'mdi-phone',
    value: '033-2988572'
  },
  {
    title: 'Email',
    icon: 'mdi-email',
    value: 'scriba@ngkeemdijk.nl'
  }
];
</script>

<style scoped>
.app-on-primary {
  color: rgb(var(--v-theme-on-primary)) !important;
}

:deep(.app-toolbar-action),
:deep(.app-toolbar-action .v-btn__content),
:deep(.app-toolbar-action .v-icon),
:deep(.app-on-primary .v-list-item-title),
:deep(.app-on-primary .v-icon) {
  color: rgb(var(--v-theme-on-primary)) !important;
}
</style>
